// Component: SmartTradeSettings.jsx
// Purpose: Context-aware Algo tab — deployment context shows the running strategy
//          from live trading data; instrument context lists running deployments on
//          that instrument, each clicking through to its Live Analysis tab (D42/R4.1-R4.3)

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTradingData } from '../../contexts/TradingDataContext';
import AlgoDashboardService from '../../api/services/algoDashboard';

/*>>> DEPLOYMENT CONTEXT (R4.1) <<<*/
//*  Reads the SAME `deployment` record LiveAnalysisTab/StrategyDeepDive already hold via
//*  useTradingData().deployments — NO new fetch. Only valid when mounted inside
//*  TradingDataLayout (StrategyDeepDive/LiveAnalysisTab), never from SmartTrade/WatchList.
const DeploymentRunningStrategy = ({ deploymentId }) => {
  const { deployments } = useTradingData();
  const deployment = deployments.find((d) => d.deployment_id === deploymentId) || null;

  return (
    <div className="h-full flex flex-col gap-4">
      <h2 className="text-sm font-black text-fg-2 uppercase tracking-widest">Algo Strategy</h2>
      {deployment ? (
        <div className="p-3 rounded border border-[var(--divider)] bg-bg-3 dark:bg-bg-2">
          <div className="font-bold text-sm">
            running strategy: {deployment.strategy_name} · V{deployment.version_number} ·{' '}
            {deployment.mode} · {deployment.status}
          </div>
        </div>
      ) : (
        <div className="text-xs text-fg-3 italic">Deployment not found.</div>
      )}
    </div>
  );
};

/*>>> INSTRUMENT CONTEXT (R4.2/R4.3) <<<*/
//*  OWN fetch (not useTradingData, which would throw outside TradingDataLayout). Keyed on
//*  the PRIMITIVE instrumentId (AC-M2-5) — never on a `context` object, which would be a new
//*  reference on almost every tick on the calling SmartTrade page. Client-side filter to
//*  RUNNING deployments on this resolved instrument only (R1.3's authority, D45 item 5).
const InstrumentDeploymentsList = ({ instrumentId }) => {
  const navigate = useNavigate();
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    AlgoDashboardService.getUserDeployments(false, 1)
      .then((response) => {
        if (requestIdRef.current !== requestId) return; //-- stale response guard
        const all = response?.data?.data?.deployments || [];
        const filtered = all.filter(
          (d) =>
            (d.resolved_instrument_id ?? d.instrument_id) === instrumentId &&
            d.status === 'running'
        );
        setDeployments(filtered);
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        console.error('Failed to load instrument deployments', err);
        setError('Failed to load strategies.');
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setLoading(false);
      });
  }, [instrumentId]);

  const handleRowClick = (deploymentId) => {
    navigate(`/AlgoDashboard/${deploymentId}`, { state: { initialTab: 'analysis' } });
  };

  return (
    <div className="h-full flex flex-col gap-4">
      <h2 className="text-sm font-black text-fg-2 uppercase tracking-widest">Algo Strategy</h2>

      {loading ? (
        <div className="text-xs text-fg-3">Loading Strategies...</div>
      ) : error ? (
        <div className="text-xs text-[var(--badge-danger-fg)]">{error}</div>
      ) : deployments.length === 0 ? (
        <div className="text-xs text-fg-3 italic">
          No strategies currently deployed on this instrument.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {deployments.map((dep) => (
            <button
              key={dep.deployment_id}
              type="button"
              onClick={() => handleRowClick(dep.deployment_id)}
              className="p-3 rounded border border-[var(--divider)] bg-bg-3 dark:bg-bg-2 text-left hover:bg-bg-2"
            >
              <div className="font-bold text-sm">
                {dep.strategy_name} · V{dep.version_number}
              </div>
              <div className="text-[10px] text-fg-3">
                {dep.mode} · {dep.status}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/*>>> ENTRY POINT (D42) <<<*/
//!  `context` is REQUIRED — no fallback branch. Both callers (SmartTrade.jsx, LiveAnalysisTab)
//!  always pass it, so a fallback calling the now-deleted mock strategy-list function (api.js)
//!  mock would be dead code (M1-C6).
const SmartTradeSettings = ({ context }) => {
  if (context.kind === 'deployment') {
    return <DeploymentRunningStrategy deploymentId={context.deploymentId} />;
  }
  return <InstrumentDeploymentsList instrumentId={context.instrumentId} />;
};

export default SmartTradeSettings;
